<template>
    <vx-expandable-card>
        <template v-slot:header> When </template>
        <template v-slot:headerSubtitle> Tell us when you’d like your campaign to run. </template>

        <template v-slot:content>
            <campaign-step-form-when-duration-type class="mt-4 mb-8"
                :modelValue="props.durationType"
                :campaignId="props.campaignId"
                :disabled="!!props.campaignId"
                @update:modelValue="emit('update:durationType', $event)"
                :isRepeatable="props.isRepeatable"
                @update:isRepeatable="emit('update:isRepeatable', $event)"></campaign-step-form-when-duration-type>
            <div class="">
                <div class="text-sm text-slate-800 font-extrabold">
                    Start on
                </div>
                <div class="text-sm text-slate-500">
                    <span v-if="!props.disabled">
                        Times shown in your preferred time-zone ({{ timezone }}).
                    </span>
                </div>
                <div v-if="props.disabled"
                    class="pt-6 text-slate-400 text-center">
                    <campaign-card-dates :modelValue="{
                        status: props.campaignStatus,
                        started_at: props.campaignStartedAt,
                        duration_type: props.durationType,
                        live_at: props.originalLiveAt,
                        time_zone_aware: props.timeZoneAware,
                    }"></campaign-card-dates>
                </div>
                <div v-else class="flex flex-col items-center justify-center mt-6">
                    <vx-date-picker
                        attached
                        setsToNow
                        isRequired
                        v-model="liveAt"
                        mode="datetime"
                        :timezone="getClientTz()"
                        :minDate="dayjs().tz(getClientTz()).add(4, 'minutes').toDate()"></vx-date-picker>
                    <div v-if="displayFirstError(validation.$errors)"
                        class="text-xs text-rose-800 mt-2"
                        aria-invalid="true">{{ displayFirstError(validation.$errors) }}</div>

                    <vx-alert color="info"
                        class="mt-6">
                        You should respect safe sending hours. Texting is allowed between 8 am to 9 pm in the contact's time-zone. We recommend scheduling between 10 am to 6 pm to drive the most engagement.
                    </vx-alert>

                    <campaign-step-form-when-time-zone-aware
                        v-if="FEATURES.time_zone_aware_campaigns"
                        :disabled="props.disabled"
                        :liveAt="liveAt"
                        :timeZoneAware="props.timeZoneAware"
                        @update:timeZoneAware="emit('update:timeZoneAware', $event)">
                    </campaign-step-form-when-time-zone-aware>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import {
    VxAlert,
    VxDatePicker,
    VxExpandableCard,
} from '@voxie/frontend-components';
import { onMounted, inject, computed } from 'vue';
import { formatTz, getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs'
import CampaignCardDates from '~/components/campaigns/campaign-card/CampaignCardDates.vue';
import useVuelidate from "@vuelidate/core";
import { isAfterDate, displayFirstError } from "~/utils/validation";
import CampaignStepFormWhenTimeZoneAware from "./CampaignStepFormWhenTimeZoneAware.vue";
import CampaignStepFormWhenDurationType from "./CampaignStepFormWhenDurationType.vue";

const FEATURES = inject('FEATURES');
const props = defineProps({
    campaignId: {
        type: Number,
    },
    campaignStatus: {
        type: String,
    },
    campaignStartedAt: {
        type: String,
    },
    durationType: {
        type: String,
        required: true,
    },
    originalLiveAt: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
    },
    timeZoneAware: {
        type: Boolean,
        default: false,
    },
    isRepeatable: {
        type: Boolean,
    },
});

const emit = defineEmits(['update:durationType', 'update:liveAt', 'update:timeZoneAware', 'update:isRepeatable', 'update:repeatDelay']);

const timezone = formatTz(dayjs().tz(getClientTz()).format('z'));

const liveAt = defineModel('liveAt', {
        type: Date,
        required: true,
    })

const validation = useVuelidate({
    liveAt: props.disabled ? {} : {
        isBefore5Hours: isAfterDate({
            message: 'Start date is not in the past 5 hours',
            date: () => dayjs().subtract('5', 'hours').toDate()
        }),
    }
}, computed(() => ({liveAt})));

onMounted(() => {
    if (!props.durationType) {
        emit('update:durationType', 'one_time');
    }
});

const validate = async () => {
    // force the reevaluation of the field validation, necessary
    // when the user doesn't change the value manually
    const previousValue = validation.value.liveAt.$model;
    validation.value.liveAt.$model = null;
    validation.value.liveAt.$model = previousValue;
    return validation.value.$validate()
}

defineExpose({
    validate,
})
</script>
